import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  subscriptionService,
  Subscription,
  ExtendSubscriptionRequest,
} from "@/services/subscription";
import { EPaymentStatus, EPaymentMethod } from "@/services/payment";
import { Loader2 } from "lucide-react";
import { formatCurrency } from "@/lib/utils";

interface ExtendSubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: Subscription | null;
}

export function ExtendSubscriptionModal({
  open,
  onOpenChange,
  subscription,
}: ExtendSubscriptionModalProps) {
  const queryClient = useQueryClient();

  const [extendForm, setExtendForm] = useState<ExtendSubscriptionRequest>({
    paymentMethod: EPaymentMethod.TRANSFER,
    paymentStatus: EPaymentStatus.COMPLETED,
  });

  useEffect(() => {
    if (open) {
      setExtendForm({
        paymentMethod: EPaymentMethod.TRANSFER,
        paymentStatus: EPaymentStatus.COMPLETED,
      });
    }
  }, [open]);

  const extendSubscriptionMutation = useMutation({
    mutationFn: (data: ExtendSubscriptionRequest) =>
      subscriptionService.extend(subscription!.id, data),
    onSuccess: () => {
      toast.success("Suscripción extendida exitosamente");
      queryClient.invalidateQueries({ queryKey: ["subscription", subscription?.id] });
      queryClient.invalidateQueries({ queryKey: ["payments", "subscription", subscription?.id] });
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleExtend = () => {
    if (subscription) {
      extendSubscriptionMutation.mutate(extendForm);
    }
  };

  // Calculate new end date
  const getNewEndDate = () => {
    if (!subscription) return "";
    const start = new Date(subscription.startDate);
    const end = new Date(subscription.endDate);
    const duration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const newEnd = new Date(subscription.endDate);
    newEnd.setDate(newEnd.getDate() + duration);
    return newEnd.toLocaleDateString("es-CO");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Extender Suscripción</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {/* Información de la extensión */}
          {subscription && (
            <div className="p-3 bg-primary/10 rounded-lg border border-primary/20 space-y-2">
              <p className="text-sm font-medium text-primary">Detalles de la extensión</p>
              <div className="text-sm space-y-1">
                <p className="text-muted-foreground">
                  Cliente: <span className="font-medium text-foreground">{subscription.client?.name}</span>
                </p>
                <p className="text-muted-foreground">
                  Slot: <span className="font-medium text-foreground">{subscription.subscriptionItems?.[0]?.slot?.name || "Cuenta completa"}</span>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm pt-2 border-t border-primary/20">
                <div>
                  <p className="text-muted-foreground">Precio a cobrar</p>
                  <p className="font-semibold text-lg">${formatCurrency(Number(subscription.price))}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Nueva fecha de fin</p>
                  <p className="font-semibold">{getNewEndDate()}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="extendPaymentMethod">Método de pago</Label>
            <Select
              value={extendForm.paymentMethod}
              onValueChange={(value) =>
                setExtendForm((prev) => ({
                  ...prev,
                  paymentMethod: value as EPaymentMethod,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un método" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={EPaymentMethod.TRANSFER}>Transferencia</SelectItem>
                <SelectItem value={EPaymentMethod.CASH}>Efectivo</SelectItem>
                <SelectItem value={EPaymentMethod.NEQUI}>Nequi</SelectItem>
                <SelectItem value={EPaymentMethod.DAVIPLATA}>Daviplata</SelectItem>
                <SelectItem value={EPaymentMethod.BANCOLOMBIA}>Bancolombia</SelectItem>
                <SelectItem value={EPaymentMethod.PAYPAL}>Paypal</SelectItem>
                <SelectItem value={EPaymentMethod.BINANCE}>Binance</SelectItem>
                <SelectItem value={EPaymentMethod.YAPE}>Yape</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="extendPaymentStatus">Estado del pago</Label>
            <Select
              value={extendForm.paymentStatus}
              onValueChange={(value) =>
                setExtendForm((prev) => ({
                  ...prev,
                  paymentStatus: value as EPaymentStatus,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={EPaymentStatus.COMPLETED}>Completado</SelectItem>
                <SelectItem value={EPaymentStatus.PENDING}>Pendiente</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleExtend}
            disabled={extendSubscriptionMutation.isPending || !subscription}
          >
            {extendSubscriptionMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Extender
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
